// Customer testimonials shown on the home page and the SEO landing pages.
//
// ONLY real, approved customer quotes belong here, never placeholder or
// invented ones (fake testimonials are deceptive, and fake review markup can
// draw a Google penalty). The Testimonials section renders nothing while this
// list is empty, so shipping it empty is safe.
//
// To add a testimonial: fill in both `tr` and `en` for the quote (and role,
// when set); the pages are bilingual and there are no fallback strings.

export type TestimonialLang = "tr" | "en";

/**
 * The real-world deployment story ("dogfooding"), shown alongside (or, while
 * the quote list is empty, instead of) customer testimonials. Deliberately
 * framed as "we run our own station on this" rather than a customer quote:
 * the station is operated by the product's maker, and presenting that as an
 * independent testimonial would be misleading.
 */
export interface FieldProven {
  station: string;
  /** Fuel brand the station operates under (text only, no third-party logos). */
  brand: string;
  websiteUrl: string;
  mapsUrl: string;
  /**
   * Condensed one-line version of the same claim, for the proof strip shown
   * high on the home page (the full `description` stays in the lower section).
   */
  headline: Record<TestimonialLang, string>;
  description: Record<TestimonialLang, string>;
}

export const fieldProven: FieldProven = {
  station: "Vahdet Petrol",
  brand: "Shell",
  websiteUrl: "https://vahdetpetrol.com.tr/",
  mapsUrl: "https://maps.app.goo.gl/fZNCuUpJgkiZtaat6",
  headline: {
    tr: "Kendi Shell istasyonumuz Vahdet Petrol'ü her gün Shift Tracker ile yönetiyoruz.",
    en: "We run our own Shell station, Vahdet Petrol, on Shift Tracker every single day.",
  },
  description: {
    tr: "Shift Tracker'ı kendi istasyonumuz için geliştirdik: Shell bayisi Vahdet Petrol'ün günlük vardiya operasyonu, kart satışı mutabakatı ve nakit takibi her gün Shift Tracker ile yürüyor.",
    en: "We built Shift Tracker for our own station: the daily shift operations, card-sale reconciliation and cash tracking of Vahdet Petrol, a Shell-branded station, run on Shift Tracker every single day.",
  },
};

export interface Testimonial {
  /** Stable key, e.g. "yilmaz-petrol". */
  id: string;
  /** Person's display name, e.g. "Mehmet Yılmaz". */
  name: string;
  /** Station/company name, shown under the person's name. */
  company: string;
  /** Optional role, per language (e.g. tr: "İstasyon Sahibi", en: "Station Owner"). */
  role?: Record<TestimonialLang, string>;
  /** The quote itself, per language. */
  quote: Record<TestimonialLang, string>;
}

export const testimonials: Testimonial[] = [
  // Example shape (do not uncomment without a real, approved quote):
  // {
  //   id: "example-petrol",
  //   name: "Ad Soyad",
  //   company: "Örnek Petrol",
  //   role: { tr: "İstasyon Sahibi", en: "Station Owner" },
  //   quote: {
  //     tr: "Shift Tracker ile vardiya açıklarını ilk haftada yakaladık.",
  //     en: "With Shift Tracker we caught shift deficits in the first week.",
  //   },
  // },
];
